(() => {
  if (customElements.get('lisa-category-arrivals')) return;
  const lifetime = 3600000;
  const windowSeconds = 30 * 86400;
  const pending = new Map();
  const queue = [];
  let active = 0;
  const pump = () => {
    while (active < 2 && queue.length) {
      const job = queue.shift();
      active++;
      job.run().then(job.resolve, job.reject).finally(() => { active--; pump(); });
    }
  };
  const enqueue = (run) => new Promise((resolve, reject) => { queue.push({ run, resolve, reject }); pump(); });
  const read = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key));
      if (value && Number.isFinite(value.at) && Number.isFinite(value.now) && Date.now() >= value.at && Date.now() - value.at < lifetime && Array.isArray(value.dates) && value.dates.every(Number.isFinite)) return value;
    } catch {}
    return null;
  };
  const fetchDates = async (href) => {
    const url = new URL(href, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid collection');
    url.search = '';
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('section_id', 'lisa-category-arrivals-data');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const dates = [];
    let page = 1;
    let now;
    do {
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'same-origin', signal: AbortSignal.timeout(8000) });
      if (!response.ok) throw new Error('Count unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const element = html.querySelector('[data-lisa-new-data]');
      if (!element) throw new Error('Count unavailable');
      const data = JSON.parse(element.textContent);
      if (!data.valid || !Number.isFinite(data.now) || !Array.isArray(data.dates) || !data.dates.every(Number.isFinite)) throw new Error('Invalid count');
      dates.push(...data.dates);
      now = data.now;
      if (data.next !== null && (data.next !== page + 1 || data.next > 500)) throw new Error('Invalid page');
      page = data.next;
    } while (page);
    return { at: Date.now(), now, dates };
  };
  class CategoryArrivals extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      const badges = [...this.querySelectorAll('[data-new-arrivals]')];
      const load = (badge) => {
        const key = 'lisa-arrivals-v1:' + this.dataset.cacheScope + ':' + badge.dataset.collectionUrl;
        const cached = read(key);
        if (cached) { this.paint(badge, cached); return; }
        if (!pending.has(key)) {
          const promise = enqueue(() => fetchDates(badge.dataset.collectionUrl)).then((value) => {
            try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
            return value;
          });
          pending.set(key, promise);
          promise.finally(() => pending.delete(key)).catch(() => {});
        }
        pending.get(key).then((value) => { if (badge.isConnected) this.paint(badge, value); }).catch(() => {});
      };
      const start = () => {
        if (!this.isConnected) return;
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.observer.unobserve(entry.target);
              load(entry.target.querySelector('[data-new-arrivals]'));
            });
          }, { rootMargin: '100px 0px' });
          badges.forEach((badge) => {
            const key = 'lisa-arrivals-v1:' + this.dataset.cacheScope + ':' + badge.dataset.collectionUrl;
            const cached = read(key);
            if (cached) this.paint(badge, cached);
            else this.observer.observe(badge.parentElement);
          });
        } else badges.forEach(load);
      };
      this.start = () => {
        if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
        else setTimeout(start, 0);
      };
      if (document.readyState === 'complete') this.start();
      else window.addEventListener('load', this.start, { once: true });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('load', this.start);
      this.started = false;
    }
    paint(badge, value) {
      const now = value.now + Math.max(0, Date.now() - value.at) / 1000;
      const count = value.dates.filter((date) => date >= now - windowSeconds && date <= now).length;
      badge.hidden = count === 0;
      if (!count) return;
      badge.textContent = count === 1 ? badge.dataset.labelOne : badge.dataset.label.replace('[count]', String(count));
      badge.setAttribute('aria-label', count === 1 ? badge.dataset.descriptionOne : badge.dataset.description.replace('[count]', String(count)));
    }
  }
  customElements.define('lisa-category-arrivals', CategoryArrivals);
})();
